import React, { useState, useEffect } from "react";
import { Dropdown, Button, Empty, message, Popconfirm, Tooltip } from "antd";
import { BookOutlined, DeleteOutlined } from "@ant-design/icons";
import { getSavedViews, deleteSavedView } from "Explore/Services/SavedViews";
import styles from "./SavedViewsDropdown.module.scss";
import moment from "moment";

const SavedViewsDropdown = ({ onRestoreView, onSaveCurrentView, refreshTrigger, disableSave }) => {
  const [views, setViews] = useState([]);
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(false);

  const fetchViews = async () => {
    setLoading(true);
    try {
      const data = await getSavedViews();
      setViews(data?.items || []);
    } catch (error) {
      console.error("Failed to fetch saved views:", error);
      // Fail silently if there's no BE yet or token issues
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchViews();
  }, [refreshTrigger]);

  const handleDelete = async (e, id) => {
    e.stopPropagation();
    try {
      await deleteSavedView(id);
      message.success("Saved view deleted");
      setViews(views.filter(v => v.id !== id));
    } catch (error) {
      console.error("Failed to delete saved view:", error);
      message.error("Failed to delete saved view");
    }
  };

  const handleMenuClick = (e) => {
    if (e.key === 'save_current') {
      if (onSaveCurrentView && !disableSave) {
        onSaveCurrentView();
      }
      setOpen(false);
      return;
    }

    const view = views.find(v => v.id === e.key);
    if (view) {
      onRestoreView(view);
      setOpen(false);
    }
  };

  const menuItems = [
    {
      key: 'save_current',
      disabled: disableSave,
      label: (
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', color: disableSave ? undefined : 'var(--primary-color, #1890ff)', fontWeight: 500 }}>
          <BookOutlined /> Save Current View
        </div>
      )
    },
    { type: 'divider' }
  ];

  if (views.length > 0) {
    views.forEach(view => {
      menuItems.push({
        key: view.id,
        label: (
          <div className={styles.menuItem}>
            {view.entity_avatar && (
              <div className={styles.avatar}>
                <img src={view.entity_avatar} alt={view.entity_name} />
              </div>
            )}
            <div className={styles.viewInfo}>
              <div className={styles.viewName}>{view.name}</div>
              <div className={styles.viewDate}>
                {(() => {
                  const rawDate = view.created_at || view.createdAt || view.updated_at || view.updatedAt || view.timestamp;
                  if (!rawDate) return "Unknown date";
                  const dateVal = typeof rawDate === 'string' ? rawDate.replace('+00:00Z', 'Z') : rawDate;
                  let m = moment(dateVal);
                  if (!m.isValid() && !isNaN(Number(dateVal))) {
                    m = moment(Number(dateVal));
                  }
                  if (m.isValid() && m.year() === 1970 && Number(dateVal) < 2000000000) {
                    m = moment(Number(dateVal) * 1000);
                  }
                  return m.isValid() ? m.fromNow() : String(dateVal);
                })()} • {view.entity_name}
              </div>
            </div>
            <Popconfirm
              title="Delete saved view?"
              onConfirm={(e) => handleDelete(e, view.id)}
              onCancel={(e) => e.stopPropagation()}
              okText="Yes"
              cancelText="No"
              placement="left"
            >
              <div
                className={styles.deleteBtn}
                onClick={(e) => e.stopPropagation()}
              >
                <Tooltip title="Delete view">
                  <DeleteOutlined />
                </Tooltip>
              </div>
            </Popconfirm>
          </div>
        )
      });
    });
  } else {
    menuItems.push({
      key: 'empty',
      disabled: true,
      label: (
        <Empty
          image={Empty.PRESENTED_IMAGE_SIMPLE}
          description="No saved views yet."
          style={{ margin: '16px 0' }}
        />
      )
    });
  }

  return (
    <Dropdown
      menu={{
        items: menuItems,
        onClick: handleMenuClick,
        className: styles.dropdownMenu
      }}
      trigger={['click']}
      open={open}
      onOpenChange={(flag) => setOpen(flag)}
      placement="bottomRight"
    >
      <Button icon={<BookOutlined />}>
        Saved Views
      </Button>
    </Dropdown>
  );
};

export default SavedViewsDropdown;
